/* ================================================================
   HYDRAVA SHOPIFY THEME — theme.js
   ================================================================ */

(function () {
  'use strict';

  /* ── Focus trap helper ──
     Focusable elements are looked up on every Tab press, so content that
     re-renders while open (cart drawer, quick-add, search results) stays trapped.
     Returns a release function. Exposed as window.Hydrava.trapFocus. */
  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  function focusablesIn(element) {
    return Array.from(element.querySelectorAll(FOCUSABLE))
      .filter(el => el.getClientRects().length > 0 && !el.closest('[inert]'));
  }
  function trapFocus(element, initial) {
    function handler(e) {
      if (e.key !== 'Tab') return;
      const focusable = focusablesIn(element);
      if (focusable.length === 0) { e.preventDefault(); return; }
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first || !element.contains(document.activeElement)) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last || !element.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
      }
    }
    element.addEventListener('keydown', handler);
    const target = initial || focusablesIn(element)[0];
    if (target) target.focus({ preventScroll: true });
    return () => element.removeEventListener('keydown', handler);
  }
  /* ── Header: transparent over a hero, solid after 70px ──
     Transparent when the header has [data-over] (Theme Editor setting,
     homepage only), body has .hv-has-hero, or the page contains a
     [data-hv-hero] element. CSS does the styling; JS only toggles .is-solid. */
  const header = document.querySelector('[data-site-header]');
  function headerOver() {
    return !!header && (header.hasAttribute('data-over') ||
      document.body.classList.contains('hv-has-hero') ||
      !!document.querySelector('[data-hv-hero]'));
  }
  function updateHeader() {
    if (!header) return;
    header.classList.toggle('is-solid', !headerOver() || window.scrollY > 70);
  }
  if (header) {
    let headerTick = false;
    window.addEventListener('scroll', () => {
      if (headerTick) return;
      headerTick = true;
      requestAnimationFrame(() => { headerTick = false; updateHeader(); });
    }, { passive: true });
    updateHeader();
    // Theme Editor: sections can be added/removed under the header
    document.addEventListener('shopify:section:load', updateHeader);
    document.addEventListener('shopify:section:unload', () => setTimeout(updateHeader, 0));
  }

  /* ── Mobile menu (slide-down panel #menu-drawer) ──
     The toggle lives in the header and morphs to an X, so the focus trap
     covers the header + panel together (both inside the header section). */
  const menuOverlay  = document.getElementById('menu-overlay');
  const menuDrawer   = document.getElementById('menu-drawer');
  const menuToggle   = document.querySelector('.header-menu-toggle');
  const menuTrapRoot = menuDrawer && (menuDrawer.closest('.shopify-section') || menuDrawer.parentElement);

  let _menuTrapRelease = null;
  let _menuTrigger = null;
  function openMenu() {
    if (!menuDrawer || menuDrawer.classList.contains('open')) return;
    _menuTrigger = document.activeElement;
    menuDrawer.classList.add('open');
    if (menuOverlay) menuOverlay.classList.add('open');
    document.body.classList.add('menu-open');
    document.body.style.overflow = 'hidden';
    if (menuToggle) {
      menuToggle.setAttribute('aria-expanded', 'true');
      menuToggle.setAttribute('aria-label', menuToggle.dataset.labelClose || 'Close menu');
    }
    _menuTrapRelease = trapFocus(menuTrapRoot, menuDrawer.querySelector('a[href]') || menuToggle);
  }
  function closeMenu(restoreFocus) {
    if (!menuDrawer || !menuDrawer.classList.contains('open')) return;
    menuDrawer.classList.remove('open');
    if (menuOverlay) menuOverlay.classList.remove('open');
    document.body.classList.remove('menu-open');
    document.body.style.overflow = '';
    if (menuToggle) {
      menuToggle.setAttribute('aria-expanded', 'false');
      menuToggle.setAttribute('aria-label', menuToggle.dataset.labelOpen || 'Open menu');
    }
    if (_menuTrapRelease) { _menuTrapRelease(); _menuTrapRelease = null; }
    if (restoreFocus !== false && _menuTrigger && _menuTrigger.focus) _menuTrigger.focus();
    _menuTrigger = null;
  }
  if (menuToggle) menuToggle.addEventListener('click', () => {
    if (menuDrawer && menuDrawer.classList.contains('open')) closeMenu(); else openMenu();
  });
  if (menuOverlay) menuOverlay.addEventListener('click', () => closeMenu());
  if (menuDrawer) menuDrawer.addEventListener('click', e => { if (e.target.closest('a[href]')) closeMenu(false); });
  // Close the panel if the window grows past the phone breakpoint
  if (window.matchMedia) {
    const desktopMQ = window.matchMedia('(min-width: 761px)');
    const onMQ = e => { if (e.matches) closeMenu(false); };
    if (desktopMQ.addEventListener) desktopMQ.addEventListener('change', onMQ);
    else if (desktopMQ.addListener) desktopMQ.addListener(onMQ);
  }

  /* ── Money (currency-aware) ──
     Under Shopify Markets, cart amounts come back in the visitor's checkout
     currency; format them in that currency (symbol/decimals per region).
     Falls back to the shop's money_format. Exposed as window.Hydrava.money. */
  const _moneyFormat = document.getElementById('money-format')?.dataset.format || '${{amount}}';
  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const amountNoDecimals = Math.round(cents / 100).toString();
    return _moneyFormat
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimals)
      .replace(/<[^>]*>/g, '');
  }
  function cartCurrencyCode(cart) {
    const fmtEl = document.getElementById('money-format');
    return String((cart && cart.currency) || fmtEl?.dataset.currency || 'USD').toUpperCase();
  }
  // "$9.99", or "$9.99 AUD" when Theme settings → Country and currency → Show currency codes is on,
  // matching Liquid's money_with_currency so dollar markets (AUD, USD, CAD, NZD) can't be confused.
  function showCurrencyCode() {
    return document.getElementById('money-format')?.dataset.currencyCode === 'true';
  }
  function money(cents, currency) {
    const cur = String(currency || cartCurrencyCode()).toUpperCase();
    let out;
    try {
      out = new Intl.NumberFormat(undefined, { style: 'currency', currency: cur, currencyDisplay: 'narrowSymbol' }).format((cents || 0) / 100);
    } catch (e) {
      try {
        out = new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format((cents || 0) / 100);
      } catch (e2) {
        out = formatMoney(cents || 0);
      }
    }
    return showCurrencyCode() && out.indexOf(cur) === -1 ? out + ' ' + cur : out;
  }

  /* ── Free-shipping threshold ──
     Chosen per currency from the merchant's map (e.g. "AUD: 100 | GBP: 60"),
     matching the visitor's checkout currency, then the single default amount.
     Amounts accept "100", "$100", "100.00". Returns cents.
     Exposed as window.Hydrava.freeShipping(currency) → { enabled, threshold, currency }. */
  function freeShipping(currency) {
    const fmtEl = document.getElementById('money-format');
    const cur = String(currency || cartCurrencyCode()).toUpperCase();
    // Country rules first ("AU: 45 AUD"). Amounts in the shop currency are converted with Shopify's rate.
    const countryRaw = fmtEl?.dataset.freeShipCountries || '';
    if (countryRaw.trim()) {
      const country = String(fmtEl?.dataset.country || '').toUpperCase();
      const shopCur = String(fmtEl?.dataset.shopCurrency || '').toUpperCase();
      const enabledC = (fmtEl?.dataset.freeShipEnabled || 'true') !== 'false';
      let hit = null;
      countryRaw.split('|').forEach(row => {
        const i = row.indexOf(':'); if (i === -1) return;
        if (row.slice(0, i).trim().toUpperCase() !== country) return;
        const parts = row.slice(i + 1).trim().split(/\s+/);
        const amt = parseFloat(String(parts[0] || '').replace(/[^0-9.]/g, ''));
        const rowCur = String(parts[1] || cur).toUpperCase();
        if (!amt) return;
        let cents = Math.round(amt * 100);
        if (rowCur !== cur) {
          const rate = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
          if (rowCur === shopCur && rate) cents = Math.round(cents * rate); else return;
        }
        hit = cents;
      });
      return { enabled: enabledC && !!hit, threshold: hit || 0, currency: cur };
    }
    let threshold = null;
    const mapRaw = fmtEl?.dataset.freeShipMap || '';
    if (mapRaw) {
      mapRaw.split(/[|,;\n]/).forEach(pair => {
        const idx = pair.indexOf(':');
        if (idx === -1) return;
        const code = pair.slice(0, idx).trim().toUpperCase();
        const amt  = parseFloat(pair.slice(idx + 1).replace(/[^0-9.]/g, ''));
        if (code === cur && amt) threshold = Math.round(amt * 100);
      });
    }
    if (!threshold) {
      const rawThresh = (fmtEl?.dataset.freeShipAmount || '').replace(/[^0-9.]/g, '');
      threshold = Math.round(parseFloat(rawThresh) * 100);
    }
    if (!threshold || isNaN(threshold)) threshold = 10000; // final fallback (100 in cents)
    return { enabled: (fmtEl?.dataset.freeShipEnabled || 'true') !== 'false', threshold, currency: cur };
  }

  /* ── Cart drawer ──
     theme.js owns the drawer behaviour (open/close, focus trap, Escape, the
     Cart API calls and the header count). The drawer contents are drawn by
     assets/hv-cart.js through window.HydravaCart.render(cart). */
  const cartOverlay  = document.getElementById('cart-overlay');
  const cartDrawer   = document.getElementById('cart-drawer');

  let _cartTrapRelease = null;
  let _cartTrigger = null;
  function cartIsOpen() { return !!cartDrawer && cartDrawer.classList.contains('open'); }
  function openCart() {
    if (!cartDrawer) return;
    if (cartIsOpen()) { renderCart(); return; }
    closeMenu(false);
    document.dispatchEvent(new CustomEvent('hydrava:overlay:open', { detail: { name: 'cart' } }));
    _cartTrigger = document.activeElement;
    cartDrawer.removeAttribute('inert');
    cartDrawer.classList.add('open');
    if (cartOverlay) cartOverlay.classList.add('open');
    cartDrawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-open');
    document.body.style.overflow = 'hidden';
    renderCart();
    _cartTrapRelease = trapFocus(cartDrawer, cartDrawer.querySelector('.cart-drawer__close'));
  }
  function closeCart() {
    if (!cartIsOpen()) return;
    cartDrawer.classList.remove('open');
    if (cartOverlay) cartOverlay.classList.remove('open');
    cartDrawer.setAttribute('aria-hidden', 'true');
    cartDrawer.setAttribute('inert', '');
    document.body.classList.remove('cart-open');
    document.body.style.overflow = '';
    if (_cartTrapRelease) { _cartTrapRelease(); _cartTrapRelease = null; }
    if (_cartTrigger && document.contains(_cartTrigger) && _cartTrigger.focus) _cartTrigger.focus({ preventScroll: true });
    _cartTrigger = null;
  }
  if (cartDrawer && !cartIsOpen()) cartDrawer.setAttribute('inert', '');
  // Delegated so buttons re-rendered by the Theme Editor keep working
  document.addEventListener('click', e => {
    if (e.target.closest('[data-open-cart]')) { e.preventDefault(); openCart(); return; }
    if (e.target.closest('.cart-drawer__close, [data-close-cart]')) { closeCart(); return; }
    if (cartOverlay && e.target === cartOverlay) closeCart();
  });

  /* ── Escape key closes open drawers ──
     (hv-cart.js closes its quick-add and search dialogs first, in the capture phase.) */
  document.addEventListener('keydown', e => {
    if (e.key !== 'Escape') return;
    if (cartIsOpen()) closeCart();
    if (menuDrawer && menuDrawer.classList.contains('open')) closeMenu();
  });

  /* ── Cart API helpers ── */
  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    return res.json();
  }
  // addToCart(variantId, qty = 1, opts = {})
  //   opts.openDrawer === false → don't open the drawer (caller opens it after its own animation).
  //   opts.bump === false → skip the badge bump / bag tug (caller bumps when its fly-to-cart lands).
  // Resolves with the /cart/add.js response; rejects with an Error (Shopify's message) on failure.
  // Existing one- and two-argument callers keep working unchanged.
  async function addToCart(variantId, qty = 1, opts = {}) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      // variantId may also be an items array ([{ id, quantity }, …]) to add several lines at once
      body: JSON.stringify({ items: Array.isArray(variantId) ? variantId : [{ id: variantId, quantity: qty }] })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      updateCartCount();
      const err = new Error(data.description || data.message || 'Could not add to bag');
      err.status = res.status;
      throw err;
    }
    if (!opts || opts.bump !== false) bumpCart();
    document.dispatchEvent(new CustomEvent('hydrava:cart:updated', { detail: { added: data } }));
    if (!opts || opts.openDrawer !== false) openCart();
    else renderCart();
    return data;
  }
  // changeCartItem(lineKey, qty) → resolves with the updated cart. qty 0 removes the line.
  async function changeCartItem(key, qty) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: Math.max(0, qty) })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      renderCart();
      const err = new Error(data.description || data.message || 'Could not update your bag');
      err.status = res.status;
      throw err;
    }
    renderCart(data);
    bumpCart(true);
    document.dispatchEvent(new CustomEvent('hydrava:cart:updated', { detail: { changed: key, cart: data } }));
    return data;
  }
  function setCartCount(count) {
    document.querySelectorAll('.cart-count').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }
  async function updateCartCount() {
    const cart = await fetchCart();
    setCartCount(cart.item_count);
    return cart;
  }
  // Badge bump + bag icon tug (transform-only keyframes in hv-cart.css; skipped for reduced motion)
  function bumpCart(badgeOnly) {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    document.querySelectorAll('.cart-count').forEach(el => {
      el.classList.remove('is-bump'); void el.offsetWidth; el.classList.add('is-bump');
    });
    if (badgeOnly) return;
    document.querySelectorAll('[data-open-cart]').forEach(b => {
      b.classList.remove('is-tug'); void b.offsetWidth; b.classList.add('is-tug');
    });
  }
  // renderCart(cart?) → fetches the cart unless one is passed, updates the header
  // count and hands the cart to hv-cart.js. Resolves with the cart.
  let _cartSeq = 0;
  let _lastCart = null;
  async function renderCart(cart) {
    const seq = ++_cartSeq;
    if (!cart) {
      try { cart = await fetchCart(); } catch (e) { return null; }
    }
    if (seq !== _cartSeq) return cart; // a newer render is on its way
    _lastCart = cart;
    setCartCount(cart.item_count);
    if (window.HydravaCart && typeof window.HydravaCart.render === 'function') {
      window.HydravaCart.render(cart);
    }
    return cart;
  }
  // First paint: one fetch sets the count and pre-renders the drawer
  // (hv-cart.js is deferred after this file, so wait for DOMContentLoaded).
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => renderCart());
  else renderCart();
  /* ── Bundle selector (PDP) ── */
  const bundleOptions = document.querySelectorAll('.bundle-option');
  const priceDisplay  = document.getElementById('pdp-price');
  const priceSave     = document.getElementById('pdp-price-save');
  const priceCompare  = document.getElementById('pdp-price-compare');
  const stickySummary = document.getElementById('sticky-bundle-summary');
  const stickyPrice   = document.getElementById('sticky-bundle-price');
  const stickySaving  = document.getElementById('sticky-bundle-saving');
  let selectedVariantId = null;

  bundleOptions.forEach(opt => {
    opt.addEventListener('click', () => {
      bundleOptions.forEach(o => o.classList.remove('selected'));
      opt.classList.add('selected');
      selectedVariantId = opt.dataset.variantId;
      const price   = opt.dataset.price;
      const compare = opt.dataset.compare;
      const saving  = opt.dataset.saving;
      const label   = opt.dataset.label;
      if (priceDisplay)  priceDisplay.textContent  = price;
      if (priceCompare)  { priceCompare.textContent = compare || ''; priceCompare.style.display = compare ? 'inline' : 'none'; }
      if (priceSave)     { priceSave.textContent = saving || ''; priceSave.style.display = saving ? 'inline-flex' : 'none'; }
      if (stickySummary) stickySummary.textContent = label;
      if (stickyPrice)   stickyPrice.textContent   = price;
      if (stickySaving)  { stickySaving.textContent = saving || ''; stickySaving.style.display = saving ? 'inline-flex' : 'none'; }
    });
  });
  if (bundleOptions.length > 0) bundleOptions[0].click();

  /* ── Add to cart button (PDP) ── */
  const addBtn = document.getElementById('pdp-add-to-cart');
  if (addBtn) {
    addBtn.addEventListener('click', async () => {
      if (!selectedVariantId) return;
      addBtn.textContent = 'Adding…';
      addBtn.disabled = true;
      await addToCart(selectedVariantId, 1);
      addBtn.textContent = '✓ Added to bag!';
      addBtn.style.background = 'var(--teal)';
      setTimeout(() => {
        addBtn.textContent = 'Add to bag';
        addBtn.style.background = '';
        addBtn.disabled = false;
      }, 2200);
    });
  }

  /* ── Colour selector (PDP) ── */
  document.querySelectorAll('.colour-option').forEach(opt => {
    if (opt.classList.contains('sold-out')) return;
    opt.addEventListener('click', () => {
      const group = opt.closest('.colour-selector');
      group.querySelectorAll('.colour-option').forEach(o => o.classList.remove('selected'));
      opt.classList.add('selected');
      // Update gallery if images provided
      const imgSrc = opt.dataset.image;
      const galleryMain = document.querySelector('.pdp__gallery-main img');
      if (galleryMain && imgSrc) galleryMain.src = imgSrc;
    });
  });

  /* ── Gallery thumbnails ── */
  document.querySelectorAll('.pdp__gallery-thumb').forEach((thumb, i) => {
    thumb.addEventListener('click', () => {
      document.querySelectorAll('.pdp__gallery-thumb').forEach(t => t.classList.remove('active'));
      thumb.classList.add('active');
      const main = document.querySelector('.pdp__gallery-main img');
      if (main && thumb.dataset.src) main.src = thumb.dataset.src;
    });
  });

  /* ── Product tabs ── */
  document.querySelectorAll('.tab-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const panel = btn.closest('.pdp__tabs-wrapper') || document;
      panel.querySelectorAll('.tab-btn').forEach(b => b.classList.remove('active'));
      panel.querySelectorAll('.tab-content').forEach(c => c.classList.remove('active'));
      btn.classList.add('active');
      const target = document.getElementById(btn.dataset.tab);
      if (target) target.classList.add('active');
    });
  });

  /* ── FAQ accordion ── */
  document.querySelectorAll('.faq-trigger').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const item = trigger.closest('.faq-item');
      const isOpen = item.classList.contains('open');
      document.querySelectorAll('.faq-item.open').forEach(el => el.classList.remove('open'));
      if (!isOpen) item.classList.add('open');
    });
  });

  /* ── Collection filters ── */
  document.querySelectorAll('.filter-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.filter-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      const filter = btn.dataset.filter;
      document.querySelectorAll('.product-card[data-tags]').forEach(card => {
        if (filter === 'all') { card.style.display = ''; return; }
        const tags = card.dataset.tags.split(',');
        card.style.display = tags.includes(filter) ? '' : 'none';
      });
    });
  });

  /* ── Quantity selector ── */
  document.querySelectorAll('.qty-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const wrapper = btn.closest('.qty-selector');
      const numEl   = wrapper.querySelector('.qty-num');
      let qty = parseInt(numEl.textContent);
      if (btn.dataset.action === 'minus') qty = Math.max(1, qty - 1);
      if (btn.dataset.action === 'plus')  qty = qty + 1;
      numEl.textContent = qty;
      const qtyInput = document.getElementById('pdp-qty');
      if (qtyInput) qtyInput.value = qty;
    });
  });

  /* ── Contact form ── */
  const contactForm = document.querySelector('.contact-form--js');
  if (contactForm) {
    contactForm.addEventListener('submit', e => {
      // Shopify handles native form submission — this just shows success state
    });
  }

  /* ── Expose add-to-cart for product cards (collection / featured grids) ── */
  /* Country / currency selector: switching country reloads the page in that market's currency */
  document.addEventListener('change', (e) => {
    const sel = e.target.closest && e.target.closest('[data-hv-loc-select]');
    if (sel && sel.form) sel.form.submit();
  });

  window.__hydravaAddToCart = addToCart;
  window.__hydravaOpenCart = openCart;
  window.__hydravaCloseCart = closeCart;
  window.__hydravaChangeCart = changeCartItem;

  /* ── Shared helpers for hv-cart.js (and any other script) ── */
  window.Hydrava = Object.assign(window.Hydrava || {}, {
    money: money,
    formatMoney: formatMoney,
    freeShipping: freeShipping,
    trapFocus: trapFocus,
    closeMenu: closeMenu,
    cart: {
      fetch: fetchCart,
      add: addToCart,
      change: changeCartItem,
      render: renderCart,
      open: openCart,
      close: closeCart,
      isOpen: cartIsOpen,
      bump: bumpCart,
      currency: cartCurrencyCode,
      last: () => _lastCart
    }
  });

})();
